import React, { useState } from "react";
import PropTypes from "prop-types";
import Modal from "../ColorOverlayModal";
import ColorPicker from "../ColorPicker";
import styles from "./ColorPickerModal.module.css";
import ColorInputBox from "../ColorInputBox/ColorInputBox";
import Button from "../Button";

export function ColorPickerModal({ isOpen, onCancel, onSave }) {
  const [colorValue, setColorValue] = useState("");

  const handleCancel = () => {
    onCancel();
    setColorValue("");
  };

  const handleSave = () => {
    onSave(colorValue);
    setColorValue("");
  };

  // Add this helper at the top or inside the component
  const getHexAlpha = (colorObj) => {
    if (typeof colorObj === "string") return colorObj;

    // Convert 0-1 alpha to 0-255 hex
    const alpha = Math.round(colorObj.rgb.a * 255)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
    return `${colorObj.hex}${alpha}`;
  };

  const handleColorChange = (newValue) => {
    if (typeof newValue === "string") {
      setColorValue(newValue);
    } else {
      const { hex, rgb } = newValue;

      // If alpha is 1 (100% opaque), just use the 6-digit hex.
      // Otherwise, convert alpha to hex and append it.
      if (rgb.a === 1) {
        setColorValue(hex);
      } else {
        const alphaHex = Math.round(rgb.a * 255)
          .toString(16)
          .padStart(2, "0")
          .toUpperCase();
        setColorValue(`${hex}${alphaHex}`);
      }
    }
  };

  const options = [
    { value: "#91B6BB", label: "Light Blue (Default)" },
    { value: "#415364", label: "Slate Grey" },
    { value: "#243746", label: "Dark Blue" },
    { value: "#ADB7C0", label: "Grey" },
    { value: "#A89C96", label: "Tan" },
    { value: "#9E703C", label: "Brown" },
    { value: "#FFFFFF", label: "White" },
    { value: "#000000", label: "Black" },
  ];

  const handleColorSelect = (option) => {
    const selectedColor = option.value;
    setColorValue(selectedColor);
  };

  return (
    <div className={styles.modalContent}>
      <Modal
        title="Select Color"
        isOpen={isOpen}
        onClose={handleCancel}
        className={styles.modalContainer}
      >
        <div className={styles.container}>
          <div className={styles.optionContainer}>
            <div className={styles.colorsLabel}>Colors</div>
            <div className={styles.colorOptions}>
              {options.map((option) => (
                <div
                  key={option.value}
                  className={styles.colorOption}
                  style={{ backgroundColor: option.value }}
                  onClick={() => handleColorSelect(option)}
                />
              ))}
            </div>
          </div>

          <div className={styles.colorPickerContainer}>
            <ColorPicker color={colorValue} onChange={handleColorChange} />
          </div>

          <div className={styles.inputBoxContainer}>
            <div className={styles.inputLabel}>Custom Hex</div>
            <div className={styles.colorInput}>
              <ColorInputBox
                value={colorValue}
                setValue={handleColorChange}
                placeholder="Enter color"
              />
            </div>
          </div>

          <div className={styles.buttonSection}>
            <div className={styles.button}>
              <Button className={"cancelButton"} onClick={handleCancel}>
                Cancel
              </Button>
            </div>
            <div>
              <Button className={"saveButton"} onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

ColorPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ColorPickerModal;
